import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { Breadcrumbs, Box, Divider, LinearProgress, Paper, Stack, Typography, Button, ToggleButtonGroup, ToggleButton, IconButton, Tooltip, TextField, Menu, MenuItem, Snackbar, Alert } from '@mui/material';
import { NavigateBefore, NavigateNext, ZoomIn as ZoomInIcon, ZoomOut as ZoomOutIcon, RestartAlt, Fullscreen as FullscreenIcon, FullscreenExit as FullscreenExitIcon, ArrowForward as ArrowForwardIcon, ArrowBack as ArrowBackIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getApi, makeRequest } from '@ink-ai/portal/common/requestHelper';
import { DocumentTranslationApi, GetDocumentTranslationResponseDtoTypeEnum } from '@ink-ai/insight-service-sdk';
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist/build/pdf';
import { EventBus, PDFViewer, PDFLinkService } from 'pdfjs-dist/web/pdf_viewer';
import '../../styles/pdfjs-viewer.css';
import axios from 'axios';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { useSelector } from 'react-redux';
import { RootState } from '@ink-ai/portal/reducers';
import { hasAnyRole, adminRolesForEnv, submissionRolesForEnv } from '@ink-ai/portal/common/authHelper';
// Formats handed to Collabora Online. XML is deliberately not one of them: Collabora
// has no editable XML format, so it opens define.xml read-only through Calc's XML
// import, which flattens the tree into a sheet and cannot write it back. XML tasks
// are view-only until they get an Excel-based review flow.
const isEditableOfficeFormat = (name: string): boolean => {
  return /\.(docx|pptx|xlsx)$/i.test(name || '');
};

// 增加可切换渲染方式：内置 iframe 与 pdf.js 画布渲染

// Fetch a preview PDF with a few retries. The preview PDF is regenerated
// asynchronously (and overwritten in place) after each save, and the presigned URL
// expires (~5 min), so a single attempt right after an update can transiently fail
// (URL expired, object mid-regeneration → 404/403, or a network blip). Each attempt
// requests a FRESH presigned URL, downloads it, and verifies the "%PDF" magic; on any
// failure it backs off and retries instead of silently leaving the viewer blank.
const PDF_MAGIC = [0x25, 0x50, 0x44, 0x46];

// The Collabora editor renders the document at its native (A4) width, which
// overflows the side-by-side (~half-screen) panel — users had to zoom the whole
// browser to ~70% to see the full page. Scale just the editor iframe instead.
// Tweak this value (e.g. 0.7–0.85) to taste.
const COLLABORA_ZOOM = 0.75;
async function fetchPreviewPdfWithRetry(
  id: string,
  which: 'original' | 'translated',
  attempts = 4,
  baseDelayMs = 1000,
): Promise<Uint8Array | null> {
  for (let i = 0; i < attempts; i++) {
    try {
      const json: any = await makeRequest<any>({
        method: 'GET',
        url: `/api/v1/document/translation/${id}/preview/${which}`,
        responseType: 'json',
      });
      const apiUrl: string | undefined = json?.url;
      if (apiUrl && /\.pdf(\?|$)/i.test(apiUrl)) {
        const resp = await axios.get<ArrayBuffer>(apiUrl, { responseType: 'arraybuffer' });
        const u8 = new Uint8Array(resp.data || new ArrayBuffer(0));
        if (u8.length > 4 && PDF_MAGIC.every((b, k) => u8[k] === b)) return u8;
      }
    } catch {
      // transient — fall through to backoff + retry
    }
    if (i < attempts - 1) {
      await new Promise((r) => setTimeout(r, baseDelayMs * (i + 1)));
    }
  }
  return null;
}

const Toolbar = ({ title }: { title: string }) => {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1 }}>
      <Typography variant="subtitle2" sx={{ flex: 1 }}>{title}</Typography>
    </Stack>
  );
};

export const TranslateDocumentPreview: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const authState = useSelector((state: RootState) => state.auth);
  const isAdminOrSubmission = hasAnyRole(authState.group, [...adminRolesForEnv(), ...submissionRolesForEnv()]);
  const s3OriginalUrlParam = useMemo(() => searchParams.get('s3'), [searchParams]);
  const HARDCODED_S3_URL: string | null = null; // 不再使用硬编码外链
  const [loading, setLoading] = useState(false);
  const [docName, setDocName] = useState<string | null>(searchParams.get('name'));
  const [translatedBlobUrl, setTranslatedBlobUrl] = useState<string | null>(null);
  const [originalBlobUrl, setOriginalBlobUrl] = useState<string | null>(null);
  const originalBlobUrlRef = useRef<string | null>(null);
  const translatedBlobUrlRef = useRef<string | null>(null);
  const [translatedData, setTranslatedData] = useState<Uint8Array | null>(null);
  const [originalData, setOriginalData] = useState<Uint8Array | null>(null);
  const [docType, setDocType] = useState<GetDocumentTranslationResponseDtoTypeEnum | null>(null);
  const [isOriginalPdf, setIsOriginalPdf] = useState<boolean>(true);
  const [isTranslatedPdf, setIsTranslatedPdf] = useState<boolean>(true);
  const [isOriginalImage, setIsOriginalImage] = useState<boolean>(false);
  const [isTranslatedImage, setIsTranslatedImage] = useState<boolean>(false);
  const [isOriginalTxt, setIsOriginalTxt] = useState<boolean>(false);
  const [isTranslatedTxt, setIsTranslatedTxt] = useState<boolean>(false);
  const [isOriginalExcel, setIsOriginalExcel] = useState<boolean>(false);
  const [isTranslatedExcel, setIsTranslatedExcel] = useState<boolean>(false);
  const [isOriginalHtml, setIsOriginalHtml] = useState<boolean>(false);
  const [isTranslatedHtml, setIsTranslatedHtml] = useState<boolean>(false);
  const [isOriginalXml, setIsOriginalXml] = useState<boolean>(false);
  const [isTranslatedXml, setIsTranslatedXml] = useState<boolean>(false);
  const [isOriginalXhtml, setIsOriginalXhtml] = useState<boolean>(false);
  const [isTranslatedXhtml, setIsTranslatedXhtml] = useState<boolean>(false);
  const [debugOriginal, setDebugOriginal] = useState<any | null>(null);
  const [debugTranslated, setDebugTranslated] = useState<any | null>(null);
  const [renderMode, setRenderMode] = useState<'iframe' | 'pdfjs'>('pdfjs');
  const [showDebug, setShowDebug] = useState(false); // 默认隐藏调试模块
  const [collaboraOriginalUrl, setCollaboraOriginalUrl] = useState<string | null>(null);
  const [collaboraTranslatedUrl, setCollaboraTranslatedUrl] = useState<string | null>(null);
  const [taskTranslationMode, setTaskTranslationMode] = useState<string | null>(null);
  const isSubmissionTask = taskTranslationMode === 'SUBMISSION';
  const [translatedViewMode, setTranslatedViewMode] = useState<'collabora' | 'pdf'>('pdf');
  const originalCollaboraRef = useRef<HTMLIFrameElement | null>(null);
  const translatedCollaboraRef = useRef<HTMLIFrameElement | null>(null);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  // "Open in a separate window" popup + popped-out state.
  const [openInNewAnchor, setOpenInNewAnchor] = useState<null | HTMLElement>(null);
  // When the translated editor is popped out to its own window we tear down the
  // embedded Collabora iframe so only ONE live WOPI connection remains — two live
  // views of the same doc join the same session and show co-editing cursors.
  const [poppedOutTranslated, setPoppedOutTranslated] = useState(false);
  const translatedWindowRef = useRef<Window | null>(null);
  const originalWindowRef = useRef<Window | null>(null);
  // The "open in a separate window" popup is hidden for now: cross-tab position
  // sync between the popped-out editor and this page isn't built yet. Flip to true
  // to re-enable the button — all handlers/state below stay wired.
  const SHOW_OPEN_IN_WINDOW = false;
  const collaboraPageRef = useRef<{ current: number; total: number }>({ current: 0, total: 1 });
  const pdfOriginalPageRef = useRef<{ current: number; total: number }>({ current: 1, total: 1 });
  const pdfOriginalViewerRef = useRef<any>(null);
  const pdfTranslatedPageRef = useRef<{ current: number; total: number }>({ current: 1, total: 1 });
  const pdfTranslatedViewerRef = useRef<any>(null);

  // Listen for Doc_PartChanged from Collabora to track current page
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      try {
        const msg = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
        if (msg.MessageId === 'Doc_PartChanged' && msg.Values) {
          collaboraPageRef.current = { current: msg.Values.Part ?? 0, total: msg.Values.PartCount ?? 1 };
        }
      } catch {}
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const toastRef = useRef<HTMLDivElement | null>(null);
  const showToast = (msg: string) => {
    const el = toastRef.current;
    if (!el) return;
    el.textContent = msg;
    el.style.display = 'flex';
    setTimeout(() => { el.style.display = 'none'; }, 5000);
  };

  const jumpCollaboraToPage = (iframeRef: React.MutableRefObject<HTMLIFrameElement | null>, targetPage: number) => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;
    iframe.contentWindow.postMessage(JSON.stringify({
      MessageId: 'Send_UNO_Command',
      SendTime: Date.now(),
      Values: { Command: '.uno:GotoPage', Args: '' },
    }), '*');
    showToast(`Please go to page ${targetPage}`);
  };

  const jumpPdfViewer = (viewer: any, targetPage: number) => {
    if (!viewer) return;
    try {
      const pv = viewer.getPageView?.(targetPage - 1);
      const top = pv?.div?.offsetTop;
      viewer.scrollPageIntoView({ pageNumber: targetPage, allowNegativeOffset: true });
      if (typeof top === 'number' && viewer.container) {
        setTimeout(() => { viewer.container.scrollTo({ top }); }, 50);
      }
    } catch {}
  };

  // Open the translated editor in a standalone window and detach the embedded
  // one. Reuses the already-open window (focus) instead of spawning duplicates.
  const openTranslatedInNewWindow = () => {
    setOpenInNewAnchor(null);
    if (!collaboraTranslatedUrl) return;
    if (translatedWindowRef.current && !translatedWindowRef.current.closed) {
      translatedWindowRef.current.focus();
    } else {
      translatedWindowRef.current = window.open(collaboraTranslatedUrl, `collabora-translated-${id}`, 'width=1200,height=900');
    }
    // Detach the in-page editor -> single live connection -> no co-editing UI.
    setPoppedOutTranslated(true);
  };

  // Original: open its preview (PDF) directly in a standalone window. The preview
  // presigned URL is short-lived, so fetch a fresh one on click rather than reuse
  // the one captured at page load. No co-presence concern (it's just a PDF view).
  const openOriginalInNewWindow = async () => {
    setOpenInNewAnchor(null);
    if (!id) return;
    if (originalWindowRef.current && !originalWindowRef.current.closed) {
      originalWindowRef.current.focus();
      return;
    }
    try {
      const json: any = await makeRequest<any>({
        method: 'GET',
        url: `/api/v1/document/translation/${id}/preview/original`,
        responseType: 'json',
      });
      const url: string | undefined = json?.url;
      if (url) {
        originalWindowRef.current = window.open(url, `original-preview-${id}`, 'width=1200,height=900');
      } else {
        showToast(t('translation.preview.openOriginalFailed') || '打开原文失败');
      }
    } catch (e) {
      console.warn('[Preview] open original in new window failed:', e);
      showToast(t('translation.preview.openOriginalFailed') || '打开原文失败');
    }
  };

  // Bring the translated editor back into the page: close the external window so
  // there is again exactly one live connection, then re-embed the iframe.
  const bringBackTranslated = () => {
    if (translatedWindowRef.current && !translatedWindowRef.current.closed) {
      try { translatedWindowRef.current.close(); } catch {}
    }
    translatedWindowRef.current = null;
    setPoppedOutTranslated(false);
  };

  const syncOriginalToTranslated = () => {
    const pdfInfo = pdfOriginalPageRef.current;
    if (translatedViewMode === 'collabora' && collaboraTranslatedUrl && translatedCollaboraRef.current?.contentWindow) {
      const collabInfo = collaboraPageRef.current;
      const pdfTotal = pdfInfo.total || 1;
      const collabTotal = collabInfo.total || 1;
      const targetPage = Math.max(1, Math.min(collabTotal, Math.round(pdfInfo.current / pdfTotal * collabTotal)));
      jumpCollaboraToPage(translatedCollaboraRef, targetPage);
    } else if (translatedViewMode === 'pdf' && pdfTranslatedViewerRef.current) {
      const transInfo = pdfTranslatedPageRef.current;
      const pdfTotal = pdfInfo.total || 1;
      const transTotal = transInfo.total || 1;
      const targetPage = Math.max(1, Math.min(transTotal, Math.round(pdfInfo.current / pdfTotal * transTotal)));
      jumpPdfViewer(pdfTranslatedViewerRef.current, targetPage);
    }
  };

  const syncTranslatedToOriginal = () => {
    const pdfInfo = pdfOriginalPageRef.current;
    const pdfTotal = pdfInfo.total || 1;
    let sourcePage: number;
    let sourceTotal: number;
    if (translatedViewMode === 'collabora') {
      const collabInfo = collaboraPageRef.current;
      sourcePage = collabInfo.current;
      sourceTotal = collabInfo.total || 1;
    } else {
      const transInfo = pdfTranslatedPageRef.current;
      sourcePage = transInfo.current;
      sourceTotal = transInfo.total || 1;
    }
    const targetPage = Math.max(1, Math.min(pdfTotal, Math.round(sourcePage / sourceTotal * pdfTotal)));
    jumpPdfViewer(pdfOriginalViewerRef.current, targetPage);
  };


  // Refetch the translated preview when switching to View mode
  useEffect(() => {
    let cancelled = false;
    if (translatedViewMode === 'pdf' && id && isTranslatedXml) {
      // XML has no preview PDF: reload the translated .xml itself so View shows
      // what was last saved in Collabora, not the copy loaded with the page.
      (async () => {
        try {
          const json: any = await makeRequest<any>({
            method: 'GET',
            url: `/api/v1/document/translation/${id}/preview/translated`,
            responseType: 'json',
          });
          const apiUrl: string | undefined = json?.url;
          if (!apiUrl) return;
          const resp = await axios.get<ArrayBuffer>(apiUrl, { responseType: 'arraybuffer' });
          if (cancelled) return;
          const blob = new Blob([resp.data], { type: 'application/xml' });
          const url2 = URL.createObjectURL(blob);
          if (translatedBlobUrlRef.current && translatedBlobUrlRef.current.startsWith('blob:')) URL.revokeObjectURL(translatedBlobUrlRef.current);
          setTranslatedBlobUrl(url2);
          translatedBlobUrlRef.current = url2;
        } catch (e) {
          console.warn('[Preview] Reloading translated XML failed, keeping the current view:', e);
        }
      })();
    } else if (translatedViewMode === 'pdf' && id) {
      (async () => {
        const u8 = await fetchPreviewPdfWithRetry(id, 'translated');
        if (cancelled || !u8 || u8.length === 0) return;
        setTranslatedData(u8);
        const blob = new Blob([u8], { type: 'application/pdf' });
        const url2 = URL.createObjectURL(blob);
        if (translatedBlobUrlRef.current && translatedBlobUrlRef.current.startsWith('blob:')) URL.revokeObjectURL(translatedBlobUrlRef.current);
        setTranslatedBlobUrl(url2);
        translatedBlobUrlRef.current = url2;
      })();
    }
    return () => { cancelled = true; };
  }, [translatedViewMode, id, isTranslatedXml]);

  const looksLikePdf = (buf: ArrayBuffer | Uint8Array | null | undefined): boolean => {
    if (!buf) return false;
    const u8 = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
    if (u8.length < 4) return false;
    return u8[0] === 0x25 && u8[1] === 0x50 && u8[2] === 0x44 && u8[3] === 0x46; // "%PDF"
  };

  const looksLikeImageUrl = (url: string): boolean => {
    return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(url);
  };

  const detectImageMime = (u8: Uint8Array): string | null => {
    if (u8.length >= 8 && u8[0] === 0x89 && u8[1] === 0x50 && u8[2] === 0x4E && u8[3] === 0x47) return 'image/png';
    if (u8.length >= 3 && u8[0] === 0xFF && u8[1] === 0xD8 && u8[2] === 0xFF) return 'image/jpeg';
    if (u8.length >= 6 && String.fromCharCode(u8[0], u8[1], u8[2], u8[3], u8[4], u8[5]).startsWith('GIF')) return 'image/gif';
    if (u8.length >= 12 && String.fromCharCode(u8[0], u8[1], u8[2], u8[3]) === 'RIFF' && String.fromCharCode(u8[8], u8[9], u8[10], u8[11]) === 'WEBP') return 'image/webp';
    if (u8.length >= 2 && u8[0] === 0x42 && u8[1] === 0x4D) return 'image/bmp';
    const textHead = new TextDecoder('utf-8').decode(u8.slice(0, Math.min(256, u8.length))).trim().toLowerCase();
    if (textHead.startsWith('<svg')) return 'image/svg+xml';
    return null;
  };

  const looksLikeTxtUrl = (url: string): boolean => {
    return /\.txt(\?|$)/i.test(url);
  };
  const looksLikeExcelUrl = (url: string): boolean => {
    return /\.(xlsx|xls)(\?|$)/i.test(url);
  };
  const looksLikeHtmlUrl = (url: string): boolean => {
    return /\.html?(\?|$)/i.test(url);
  };
  // An XML task's source file is served from an extensionless S3 key, so the URL
  // alone cannot tell; pass the task's file name to decide from that instead.
  const looksLikeXmlUrl = (url: string, fileName?: string): boolean => {
    return /\.xml(\?|$)/i.test(url) || /\.xml$/i.test(fileName ?? '');
  };
  const looksLikeXhtmlUrl = (url: string): boolean => {
    return /\.xhtml(\?|$)/i.test(url);
  };
  const isHtmlContent = (u8: Uint8Array): boolean => {
    const head = new TextDecoder('utf-8').decode(u8.slice(0, Math.min(1024, u8.length))).toLowerCase();
    return /<!doctype html|<html\b/.test(head);
  };
  const isXmlContent = (u8: Uint8Array): boolean => {
    const head = new TextDecoder('utf-8').decode(u8.slice(0, Math.min(1024, u8.length))).toLowerCase();
    return /<\?xml\b|<\w+[\s>]/.test(head) && !isHtmlContent(u8);
  };
  const XlsxSheetHtmlPreview: React.FC<{ srcUrl: string; title: string; kind?: 'original' | 'translated' }> = ({ srcUrl, title, kind = 'original' }) => {
    const [sheetNames, setSheetNames] = useState<string[]>([]);
    const [currentSheet, setCurrentSheet] = useState<string | null>(null);
    const [html, setHtml] = useState<string | null>(null);
    const [workbook, setWorkbook] = useState<any | null>(null);
    const [error, setError] = useState<string | null>(null);

    const [zoom, setZoom] = useState<number>(1);
    const [spW, setSpW] = useState<number>(0);
    const [spH, setSpH] = useState<number>(0);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);

    const { id } = useParams();
    const renderSheet = useMemo(() => {
      return (ws: any, XLSX: any) => {
        const ref = ws && ws['!ref'] ? ws['!ref'] : 'A1';
        const range = XLSX.utils.decode_range(ref);
        const merges = ws['!merges'] || [];
        const cols = ws['!cols'] || [];
        const mergeTopLeft: Record<string, { rs: number; cs: number }> = {};
        const covered: Record<string, boolean> = {};
        for (const m of merges) {
          const k = XLSX.utils.encode_cell({ r: m.s.r, c: m.s.c });
          const rs = m.e.r - m.s.r + 1;
          const cs = m.e.c - m.s.c + 1;
          mergeTopLeft[k] = { rs, cs };
          for (let r = m.s.r; r <= m.e.r; r++) {
            for (let c = m.s.c; c <= m.e.c; c++) {
              const ck = XLSX.utils.encode_cell({ r, c });
              covered[ck] = true;
            }
          }
          covered[k] = false;
        }
        const colgroup: string[] = [];
        for (let c = range.s.c; c <= range.e.c; c++) {
          const meta = cols[c] || {};
          const wpx = typeof meta.wpx === 'number' ? meta.wpx : (typeof meta.wch === 'number' ? Math.max(40, Math.floor(meta.wch * 7)) : 120);
          colgroup.push(`<col style="width:${wpx}px">`);
        }
        const rows: string[] = [];
        for (let r = range.s.r; r <= range.e.r; r++) {
          const cells: string[] = [];
          let emptyRow = true;
          for (let c = range.s.c; c <= range.e.c; c++) {
            const addr = XLSX.utils.encode_cell({ r, c });
            if (covered[addr]) continue;
            const cell = ws[addr];
            const text = cell ? XLSX.utils.format_cell(cell) : '';
            if (text && text.trim() !== '') emptyRow = false;
            const sp = mergeTopLeft[addr];
            const rsAttr = sp && sp.rs > 1 ? ` rowspan="${sp.rs}"` : '';
            const csAttr = sp && sp.cs > 1 ? ` colspan="${sp.cs}"` : '';
            cells.push(`<td${rsAttr}${csAttr}>${text === undefined || text === null ? '' : String(text)}</td>`);
          }
          if (!emptyRow) rows.push(`<tr>${cells.join('')}</tr>`);
        }
        return `<table><colgroup>${colgroup.join('')}</colgroup><tbody>${rows.join('')}</tbody></table>`;
      };
    }, []);
    useEffect(() => {
      let mounted = true;
      const load = async () => {
        try {
          const mod = await import('xlsx');
          const XLSX: any = (mod as any).default || mod;
          console.log('[Preview][XLSX] module loaded', typeof XLSX, Object.keys(XLSX || {}));
          let buf: ArrayBuffer | null = null;
          try {
            const resp = await axios.get<ArrayBuffer>(srcUrl, { responseType: 'arraybuffer' });
            console.log('[Preview][XLSX] fetched via srcUrl', srcUrl, 'status', (resp as any)?.status, 'len', (resp?.data as any)?.byteLength);
            buf = resp.data;
          } catch (e) {
            console.warn('[Preview][XLSX] srcUrl fetch failed, fallback api', e);
            if (id) {
              try {
                const endpoint = kind === 'translated'
                  ? `/api/v1/document/translation/${id}/preview/translated`
                  : `/api/v1/document/translation/${id}/preview/original`;
                const ab = await makeRequest<ArrayBuffer>({ method: 'GET', url: endpoint, responseType: 'arraybuffer' });
                console.log('[Preview][XLSX] fetched via api', endpoint, 'len', (ab as any)?.byteLength);
                buf = ab;
              } catch (e2) {}
            }
          }
          if (!buf) throw new Error('无法获取 Excel 内容');
          const wb = XLSX.read(buf, { type: 'array', cellDates: true, sheetStubs: true });
          console.log('[Preview][XLSX] workbook parsed', Array.isArray(wb?.SheetNames) ? wb.SheetNames.length : 0);
          setWorkbook(wb);
          setSheetNames(wb.SheetNames);
          let sheetName = wb.SheetNames[0];
          const tryNames = [...wb.SheetNames];
          for (const sn of tryNames) {
            const ws2 = wb.Sheets[sn];
            const d2: any[][] = XLSX.utils.sheet_to_json(ws2, { header: 1, blankrows: false, defval: '' });
            if (d2.length > 0) { sheetName = sn; break; }
          }
          setCurrentSheet(sheetName);
          const ws = wb.Sheets[sheetName];
          const htmlStr = renderSheet(ws, XLSX);
          setHtml(htmlStr);
        } catch (e: any) {
          console.error('[Preview][XLSX] error', e);
          setError(e?.message || 'failed to parse xlsx');
        }
      };
      load();
      return () => { mounted = false; };
    }, [srcUrl]);
    const onSheetChange = async (name: string) => {
      try {
        const mod = await import('xlsx');
        const XLSX: any = (mod as any).default || mod;
        console.log('[Preview][XLSX] switch sheet', name);
        const wb = workbook;
        if (!wb) return;
        const ws = wb.Sheets[name];
        const htmlStr = renderSheet(ws, XLSX);
        setHtml(htmlStr);
        setCurrentSheet(name);
      } catch {}
    };

    useEffect(() => {
      const el = contentRef.current;
      if (!el) return;
      const w = el.scrollWidth || el.clientWidth || 0;
      const h = el.scrollHeight || el.clientHeight || 0;
      setSpW(Math.floor(w * zoom));
      setSpH(Math.floor(h * zoom));
    }, [html, zoom]);

    if (error) {
      return <Stack sx={{ p: 2 }}><Typography variant="body2" color="error">{error}</Typography><Typography variant="body2"><a href={srcUrl} target="_blank" rel="noreferrer">在新标签页打开/下载</a></Typography></Stack>;
    }
    if (!html) {
      return <Stack sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">Loading...</Typography></Stack>;
    }
    return (
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', flex: '0 0 auto' }}>

        <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

          <TextField
            select
            size="small"
            label="Sheet"
            value={currentSheet || sheetNames[0] || ''}
            onChange={(e) => onSheetChange(e.target.value)}
            disabled={sheetNames.length === 0}
            sx={{ minWidth: 180 }}
          >
            {sheetNames.length === 0 ? (
              <MenuItem value="" disabled>无工作表</MenuItem>
            ) : (
              sheetNames.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)
            )}
          </TextField>

          <Stack direction="row" spacing={1}>
            <IconButton size="small" onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2)))}><ZoomOutIcon fontSize="small" /></IconButton>
            <IconButton size="small" onClick={() => setZoom((z) => Math.min(3, +(z + 0.1).toFixed(2)))}><ZoomInIcon fontSize="small" /></IconButton>
            <IconButton size="small" onClick={() => setZoom(1)}><RestartAlt fontSize="small" /></IconButton>
          </Stack>
        </Box>
        <Box
          ref={containerRef}
          sx={{
            position: 'relative',

            flex: 1,
            height: 600,
            maxHeight: 600,
            overflow: 'auto',
            p: 1,
            '& table': {
              width: '100%',
              borderCollapse: 'collapse',
              tableLayout: 'fixed',
              margin: 0,
            },
            '& td, & th': {
              border: '1px solid #e0e0e0',
              padding: '6px 8px',
              verticalAlign: 'top',
            },
            '& tr:nth-of-type(even) td': {
              backgroundColor: '#fafafa',
            },
            '& tr:hover td': {
              backgroundColor: '#f0f7ff',
            },
            '& td': {
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            },
          }}
        >

          <Box sx={{ width: spW, height: spH }} />
          <Box
            ref={contentRef}
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              transform: `scale(${zoom})`,
              transformOrigin: 'top left',
              pointerEvents: 'auto',
            }}
          >
            <div dangerouslySetInnerHTML={{ __html: html || '' }} />
          </Box>
        </Box>
      </Box>
    );
  };
  const isProbablyText = (u8: Uint8Array): boolean => {
    if (!u8 || u8.length === 0) return false;
    const len = Math.min(2048, u8.length);
    let nonPrintable = 0;
    for (let i = 0; i < len; i++) {
      const b = u8[i];
      if (b === 0) return false;
      if (b < 9 || (b > 13 && b < 32)) nonPrintable++;
    }
    return nonPrintable / len < 0.02;
  };

  const hex8 = (u8: Uint8Array, start: number) => Array.from(u8.slice(start, start + 8)).map((b) => b.toString(16).padStart(2, '0')).join(' ');
  const inspectPdf = (u8: Uint8Array | null) => {
    if (!u8) return { length: 0, hasPdfHeader: false, hasEof: false, headHex: '', tailHex: '', pageMarkers: 0 };
    const length = u8.length;
    const headHex = hex8(u8, 0);
    const tailHex = hex8(u8, Math.max(0, length - 8));
    const hasPdfHeader = looksLikePdf(u8);
    const dec = new TextDecoder('ascii');
    const headSample = dec.decode(u8.slice(0, Math.min(8192, length)));
    const tailSample = dec.decode(u8.slice(Math.max(0, length - 8192), length));
    const sample = headSample + tailSample;
    const hasEof = sample.includes('%%EOF');
    const pageMarkers = (sample.match(/\/(Type\s*\/Page|Page\b)/g) || []).length;
    const objCount = (sample.match(/\bobj\b/g) || []).length;
    const endobjCount = (sample.match(/\bendobj\b/g) || []).length;
    const xrefPresent = sample.includes('xref');
    const startXrefPresent = sample.includes('startxref');
    const catalogPresent = /\/(Type\s*\/Catalog|Catalog\b)/.test(sample);
    const pagesDictPresent = /\/(Pages\b|Type\s*\/Pages)/.test(sample);
    const suspiciousBlank = hasPdfHeader && (!hasEof || pageMarkers === 0 || length < 1024);
    return { length, hasPdfHeader, hasEof, headHex, tailHex, pageMarkers, objCount, endobjCount, xrefPresent, startXrefPresent, catalogPresent, pagesDictPresent, suspiciousBlank };
  };

  const sha256Hex = async (u8: Uint8Array | null) => {
    try {
      if (!u8) return null;
      const hashBuf = await crypto.subtle.digest('SHA-256', u8);
      const h = Array.from(new Uint8Array(hashBuf)).map((b) => b.toString(16).padStart(2, '0')).join('');
      return h;
    } catch (e) {
      console.warn('[Preview] SHA-256 digest failed:', e);
      return null;
    }
  };

  useEffect(() => {
    // 设置 pdf.js worker 路径（已在 webpack 中复制至 assets/pdfjs）
    try {
      GlobalWorkerOptions.workerSrc = '/assets/pdfjs/pdf.worker.min.js';
    } catch (e) {
      // 忽略在非 PDF 模式下的设置错误
    }
    const load = async () => {
      if (!id) return;
      setLoading(true);
      try {
        // 如需展示类型等元信息，可保留详情获取
        const api = await getApi(DocumentTranslationApi);
        const detail = id ? await api.getDocumentTranslation(id) : null;
        if (detail) {
          setDocType(detail.data.type);
          setTaskTranslationMode((detail.data as any).translationMode || 'STANDARD');
          // A SUBMISSION XML task is viewed in its text editor, not here. Covers
          // links and bookmarks to the preview URL; the task list goes there directly.
          if (
            (detail.data as any).translationMode === 'SUBMISSION' &&
            /\.xml$/i.test(detail.data.name || docName || '')
          ) {
            navigate(`/translate-tasks/${id}/xml-editor`, { replace: true });
            return;
          }
          // A SUBMISSION task opens in the editor so the reviewer can correct the
          // translation — but ONLY when the file is one the editor can open. A
          // SUBMISSION PDF has no Collabora session, and switching to 'collabora'
          // regardless left the pane in a mode with no editor behind it: both
          // sync handlers key off translatedViewMode, so page-jump silently did
          // nothing (collaboraPageRef is only fed by the editor's Doc_PartChanged
          // messages), and the Edit/View toggle — gated on collaboraTranslatedUrl
          // — was hidden, so there was no way back to the PDF view either.
          if (
            (detail.data as any).translationMode === 'SUBMISSION' &&
            isEditableOfficeFormat(detail.data.name || docName || '')
          ) {
            setTranslatedViewMode('collabora');
          }
          console.log('[Preview] 任务详情 type =', detail.data.type, 'uuid =', id);
          if (!docName && detail.data.name) {
            setDocName(detail.data.name);
          }
        }

        // Collabora Online: fetch editor URLs for editable Office formats
        const effectiveDocName = docName || detail?.data?.name || '';
        if (isEditableOfficeFormat(effectiveDocName) && id) {
          try {
            const transRes = await makeRequest<{ editorUrl: string; accessToken: string; wopiSrc: string }>({ method: 'GET', url: `/api/v1/document/translation/${id}/collabora-url?which=translated`, responseType: 'json' });
            if (transRes?.editorUrl && transRes?.wopiSrc && transRes?.accessToken) {
              setCollaboraTranslatedUrl(`${transRes.editorUrl}/browser/dist/cool.html?WOPISrc=${encodeURIComponent(transRes.wopiSrc)}&access_token=${transRes.accessToken}`);
            } else {
              // No usable editor session: keep the read-only PDF view rather than
              // stranding the pane in editor mode with nothing to render.
              setTranslatedViewMode('pdf');
            }
          } catch (e) {
            console.warn('[Preview] Collabora URL fetch failed, falling back to default preview:', e);
            setTranslatedViewMode('pdf');
          }
        }

        // 原文预览：若硬编码或 URL 参数提供了预签名 S3 链接，则直接使用该链接进行 iframe 渲染；否则走后端接口
        const externalS3Url: string | null = null; // 明确禁用外部 S3 参数分支，统一走后端接口
        if (id) {
          try {
            // 新接口返回 JSON，包含 S3 预签名链接
            const json: any = await makeRequest<any>({
              method: 'GET',
              url: `/api/v1/document/translation/${id}/preview/original`,
              responseType: 'json',
            });
            const apiUrl: string | undefined = json?.url;
            if (apiUrl && typeof apiUrl === 'string') {
              const looksPdf = /\.pdf(\?|$)/i.test(apiUrl);
              const img = looksLikeImageUrl(apiUrl);
              const isTxtUrl = looksLikeTxtUrl(apiUrl);
              const isXlsUrl = looksLikeExcelUrl(apiUrl);
              const isHtmlUrl = looksLikeHtmlUrl(apiUrl);
              const isXmlUrl = looksLikeXmlUrl(apiUrl, effectiveDocName);
              const isXhtmlUrl = looksLikeXhtmlUrl(apiUrl);
              setIsOriginalPdf(!!looksPdf);
              setIsOriginalImage(img);
              setIsOriginalTxt(isTxtUrl);
              setIsOriginalExcel(isXlsUrl);
              setIsOriginalHtml(isHtmlUrl);
              setIsOriginalXml(isXmlUrl);
              setIsOriginalXhtml(isXhtmlUrl);
              setOriginalData(null);
              setDebugOriginal({ from: 'api-url', url: apiUrl });
              console.log('[Preview][Original][URL] ', apiUrl);
              if (!isTxtUrl && !looksPdf && !img) {
                if (originalBlobUrl && originalBlobUrl.startsWith('blob:')) URL.revokeObjectURL(originalBlobUrl);
                setOriginalBlobUrl(apiUrl);
                originalBlobUrlRef.current = apiUrl;
              }
              if (looksLikeTxtUrl(apiUrl)) {
                try {
                  const resp = await axios.get<ArrayBuffer>(apiUrl, { responseType: 'arraybuffer' });
                  const u8 = new Uint8Array(resp.data || new ArrayBuffer(0));
                  const htmlLike = isHtmlContent(u8);
                  setIsOriginalHtml(htmlLike);
                  setIsOriginalTxt(!htmlLike);
                  const blob = new Blob([u8], { type: htmlLike ? 'text/html; charset=utf-8' : 'text/plain; charset=utf-8' });
                  const url2 = URL.createObjectURL(blob);
                  if (originalBlobUrl && originalBlobUrl.startsWith('blob:')) URL.revokeObjectURL(originalBlobUrl);
                  setOriginalBlobUrl(url2);
                  originalBlobUrlRef.current = url2;
                  setOriginalData(null);
                } catch {}
              } else if (looksPdf) {
                const u8 = await fetchPreviewPdfWithRetry(id, 'original');
                if (u8 && u8.length > 0) {
                  setOriginalData(u8);
                  const blob = new Blob([u8], { type: 'application/pdf' });
                  const url2 = URL.createObjectURL(blob);
                  if (originalBlobUrl && originalBlobUrl.startsWith('blob:')) URL.revokeObjectURL(originalBlobUrl);
                  setOriginalBlobUrl(url2);
                  originalBlobUrlRef.current = url2;
                } else {
                  setOriginalBlobUrl(apiUrl);
                  originalBlobUrlRef.current = apiUrl;
                }
              } else if (isOriginalImage) {
                try {
                  const resp = await axios.get<ArrayBuffer>(apiUrl, { responseType: 'arraybuffer' });
                  const u8 = new Uint8Array(resp.data || new ArrayBuffer(0));
                  const mime = detectImageMime(u8) || 'image/*';
                  const blob = new Blob([u8], { type: mime });
                  const url2 = URL.createObjectURL(blob);
                  if (originalBlobUrl && originalBlobUrl.startsWith('blob:')) URL.revokeObjectURL(originalBlobUrl);
                  setOriginalBlobUrl(url2);
                  originalBlobUrlRef.current = url2;
                  setOriginalData(null);
                } catch {}
              } else if (isHtmlUrl) {
                try {
                  const resp = await axios.get<ArrayBuffer>(apiUrl, { responseType: 'arraybuffer' });
                  const u8 = new Uint8Array(resp.data || new ArrayBuffer(0));
                  const blob = new Blob([u8], { type: 'text/html; charset=utf-8' });
                  const url2 = URL.createObjectURL(blob);
                  if (originalBlobUrl && originalBlobUrl.startsWith('blob:')) URL.revokeObjectURL(originalBlobUrl);
                  setOriginalBlobUrl(url2);
                  originalBlobUrlRef.current = url2;
                  setOriginalData(null);
                } catch {}
              } else if (isXmlUrl) {
                try {
                  const resp = await axios.get<ArrayBuffer>(apiUrl, { responseType: 'arraybuffer' });
                  const u8 = new Uint8Array(resp.data || new ArrayBuffer(0));
                  const blob = new Blob([u8], { type: 'application/xml' });
                  const url2 = URL.createObjectURL(blob);
                  if (originalBlobUrl && originalBlobUrl.startsWith('blob:')) URL.revokeObjectURL(originalBlobUrl);
                  setOriginalBlobUrl(url2);
                  originalBlobUrlRef.current = url2;
                  setOriginalData(null);
                } catch {}
              } else if (isXhtmlUrl) {
                try {
                  const resp = await axios.get<ArrayBuffer>(apiUrl, { responseType: 'arraybuffer' });
                  const u8 = new Uint8Array(resp.data || new ArrayBuffer(0));
                  const blob = new Blob([u8], { type: 'application/xhtml+xml' });
                  const url2 = URL.createObjectURL(blob);
                  if (originalBlobUrl && originalBlobUrl.startsWith('blob:')) URL.revokeObjectURL(originalBlobUrl);
                  setOriginalBlobUrl(url2);
                  originalBlobUrlRef.current = url2;
                  setOriginalData(null);
                } catch {}
              }
            } else {
              throw new Error('Preview original JSON has no url');
            }
          } catch (e) {
            console.warn('原文预览 JSON 链接获取失败，尝试二进制回退:', e);
            try {
              const buf: ArrayBuffer = await makeRequest<ArrayBuffer>({
                method: 'GET',
                url: `/api/v1/document/translation/${id}/preview/original`,
                responseType: 'arraybuffer',
              });
              const u8 = new Uint8Array(buf);
              const pdf = looksLikePdf(u8);
              setIsOriginalPdf(pdf);
              const mime = detectImageMime(u8);
              setIsOriginalImage(!!mime);
              const txt = isProbablyText(u8);
              const htmlLike = txt ? isHtmlContent(u8) : false;
              const xmlLike = txt ? isXmlContent(u8) : false;
              setIsOriginalHtml(htmlLike);
              setIsOriginalXml(xmlLike);
              setIsOriginalTxt(!!txt && !htmlLike && !xmlLike);
              setIsOriginalExcel(false);
              const metrics = inspectPdf(u8);
              const hash = await sha256Hex(u8);
              setDebugOriginal({ metrics, hash });
              console.log('[Preview][Original] len =', metrics.length, 'isPdf =', pdf);
              if (pdf) {
                setOriginalData(u8);
              } else {
                setOriginalData(null);
              }
              const blob = new Blob([buf], { type: pdf ? 'application/pdf' : (mime || (txt ? (htmlLike ? 'text/html; charset=utf-8' : (xmlLike ? 'application/xml' : 'text/plain; charset=utf-8')) : 'application/octet-stream')) });
              const url = URL.createObjectURL(blob);
              if (originalBlobUrl && originalBlobUrl.startsWith('blob:')) URL.revokeObjectURL(originalBlobUrl);
              setOriginalBlobUrl(url);
              originalBlobUrlRef.current = url;
            } catch (e2) {
              console.error('原文预览获取失败:', e2);
              setOriginalBlobUrl(null);
              setOriginalData(null);
              setIsOriginalPdf(false);
              setIsOriginalImage(false);
              setDebugOriginal({ error: String(e2) });
            }
          }
        }

        // 译文预览（二进制）——通过新接口获取（translated/local）
        if (id) {
          try {
            // 新接口返回 JSON，包含 S3 预签名链接
            const json: any = await makeRequest<any>({
              method: 'GET',
              url: `/api/v1/document/translation/${id}/preview/translated`,
              responseType: 'json',
            });
            const apiUrl: string | undefined = json?.url;
            if (apiUrl && typeof apiUrl === 'string') {
              const looksPdf = /\.pdf(\?|$)/i.test(apiUrl);
              const img = looksLikeImageUrl(apiUrl);
              const isTxtUrl = looksLikeTxtUrl(apiUrl);
              const isXlsUrl = looksLikeExcelUrl(apiUrl);
              const isHtmlUrl = looksLikeHtmlUrl(apiUrl);
              const isXmlUrl = looksLikeXmlUrl(apiUrl, effectiveDocName);
              const isXhtmlUrl = looksLikeXhtmlUrl(apiUrl);
              setIsTranslatedPdf(!!looksPdf);
              setIsTranslatedImage(img);
              setIsTranslatedTxt(isTxtUrl);
              setIsTranslatedExcel(isXlsUrl);
              setIsTranslatedHtml(isHtmlUrl);
              setIsTranslatedXml(isXmlUrl);
              setIsTranslatedXhtml(isXhtmlUrl);
              setTranslatedData(null);
              setDebugTranslated({ from: 'api-url', url: apiUrl });
              console.log('[Preview][Translated][URL] ', apiUrl);
              if (!isTxtUrl && !looksPdf && !img) {
                if (translatedBlobUrl && translatedBlobUrl.startsWith('blob:')) URL.revokeObjectURL(translatedBlobUrl);
                setTranslatedBlobUrl(apiUrl);
                translatedBlobUrlRef.current = apiUrl;
              }
              if (looksLikeTxtUrl(apiUrl)) {
                try {
                  const resp = await axios.get<ArrayBuffer>(apiUrl, { responseType: 'arraybuffer' });
                  const u8 = new Uint8Array(resp.data || new ArrayBuffer(0));
                  const htmlLike = isHtmlContent(u8);
                  setIsTranslatedHtml(htmlLike);
                  setIsTranslatedTxt(!htmlLike);
                  const blob = new Blob([u8], { type: htmlLike ? 'text/html; charset=utf-8' : 'text/plain; charset=utf-8' });
                  const url2 = URL.createObjectURL(blob);
                  if (translatedBlobUrl && translatedBlobUrl.startsWith('blob:')) URL.revokeObjectURL(translatedBlobUrl);
                  setTranslatedBlobUrl(url2);
                  translatedBlobUrlRef.current = url2;
                  setTranslatedData(null);
                } catch {}
              } else if (looksPdf) {
                const u8 = await fetchPreviewPdfWithRetry(id, 'translated');
                if (u8 && u8.length > 0) {
                  setTranslatedData(u8);
                  const blob = new Blob([u8], { type: 'application/pdf' });
                  const url2 = URL.createObjectURL(blob);
                  if (translatedBlobUrl && translatedBlobUrl.startsWith('blob:')) URL.revokeObjectURL(translatedBlobUrl);
                  setTranslatedBlobUrl(url2);
                  translatedBlobUrlRef.current = url2;
                } else {
                  setTranslatedBlobUrl(apiUrl);
                  translatedBlobUrlRef.current = apiUrl;
                }
              } else if (isTranslatedImage) {
                try {
                  const resp = await axios.get<ArrayBuffer>(apiUrl, { responseType: 'arraybuffer' });
                  const u8 = new Uint8Array(resp.data || new ArrayBuffer(0));
                  const mime = detectImageMime(u8) || 'image/*';
                  const blob = new Blob([u8], { type: mime });
                  const url2 = URL.createObjectURL(blob);
                  if (translatedBlobUrl && translatedBlobUrl.startsWith('blob:')) URL.revokeObjectURL(translatedBlobUrl);
                  setTranslatedBlobUrl(url2);
                  translatedBlobUrlRef.current = url2;
                  setTranslatedData(null);
                } catch {}
              } else if (isHtmlUrl) {
                try {
                  const resp = await axios.get<ArrayBuffer>(apiUrl, { responseType: 'arraybuffer' });
                  const u8 = new Uint8Array(resp.data || new ArrayBuffer(0));
                  const blob = new Blob([u8], { type: 'text/html; charset=utf-8' });
                  const url2 = URL.createObjectURL(blob);
                  if (translatedBlobUrl && translatedBlobUrl.startsWith('blob:')) URL.revokeObjectURL(translatedBlobUrl);
                  setTranslatedBlobUrl(url2);
                  translatedBlobUrlRef.current = url2;
                  setTranslatedData(null);
                } catch {}
              } else if (isXmlUrl) {
                try {
                  const resp = await axios.get<ArrayBuffer>(apiUrl, { responseType: 'arraybuffer' });
                  const u8 = new Uint8Array(resp.data || new ArrayBuffer(0));
                  const blob = new Blob([u8], { type: 'application/xml' });
                  const url2 = URL.createObjectURL(blob);
                  if (translatedBlobUrl && translatedBlobUrl.startsWith('blob:')) URL.revokeObjectURL(translatedBlobUrl);
                  setTranslatedBlobUrl(url2);
                  translatedBlobUrlRef.current = url2;
                  setTranslatedData(null);
                } catch {}
              } else if (isXhtmlUrl) {
                try {
                  const resp = await axios.get<ArrayBuffer>(apiUrl, { responseType: 'arraybuffer' });
                  const u8 = new Uint8Array(resp.data || new ArrayBuffer(0));
                  const blob = new Blob([u8], { type: 'application/xhtml+xml' });
                  const url2 = URL.createObjectURL(blob);
                  if (translatedBlobUrl && translatedBlobUrl.startsWith('blob:')) URL.revokeObjectURL(translatedBlobUrl);
                  setTranslatedBlobUrl(url2);
                  translatedBlobUrlRef.current = url2;
                  setTranslatedData(null);
                } catch {}
              }
            } else {
              throw new Error('Preview translated JSON has no url');
            }
          } catch (e) {
            console.warn('译文预览 JSON 链接获取失败，尝试二进制回退:', e);
            try {
              const buf: ArrayBuffer = await makeRequest<ArrayBuffer>({
                method: 'GET',
                url: `/api/v1/document/translation/${id}/preview/translated`,
                responseType: 'arraybuffer',
              });
              const u8 = new Uint8Array(buf);
              const pdf = looksLikePdf(u8);
              setIsTranslatedPdf(pdf);
              const mime = detectImageMime(u8);
              setIsTranslatedImage(!!mime);
              const txt = isProbablyText(u8);
              const htmlLike = txt ? isHtmlContent(u8) : false;
              const xmlLike = txt ? isXmlContent(u8) : false;
              setIsTranslatedHtml(htmlLike);
              setIsTranslatedXml(xmlLike);
              setIsTranslatedTxt(!!txt && !htmlLike && !xmlLike);
              setIsTranslatedExcel(false);
              const metrics = inspectPdf(u8);
              const hash = await sha256Hex(u8);
              const sameAsOriginal = !!debugOriginal?.hash && hash === debugOriginal.hash;
              setDebugTranslated({ metrics, hash, sameAsOriginal });
              if (pdf) {
                setTranslatedData(u8);
              } else {
                setTranslatedData(null);
              }
              const blob = new Blob([buf], { type: pdf ? 'application/pdf' : (mime || (txt ? (htmlLike ? 'text/html; charset=utf-8' : (xmlLike ? 'application/xml' : 'text/plain; charset=utf-8')) : 'application/octet-stream')) });
              const url = URL.createObjectURL(blob);
              if (translatedBlobUrl && translatedBlobUrl.startsWith('blob:')) URL.revokeObjectURL(translatedBlobUrl);
              setTranslatedBlobUrl(url);
              translatedBlobUrlRef.current = url;
            } catch (e2) {
              console.error('译文预览获取失败:', e2);
              setTranslatedBlobUrl(null);
              setTranslatedData(null);
              setIsTranslatedPdf(false);
              setIsTranslatedImage(false);
              setDebugTranslated({ error: String(e2) });
            }
          }
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  // 卸载时清理创建的 URL，避免内存泄漏
  useEffect(() => {
    // 卸载时清理最新的 URL，避免内存泄漏
    return () => {
      if (originalBlobUrlRef.current) URL.revokeObjectURL(originalBlobUrlRef.current);
      if (translatedBlobUrlRef.current) URL.revokeObjectURL(translatedBlobUrlRef.current);
    };
  }, []);

  // 通过受鉴权接口直接得到二进制，因此不再依赖外部下载链接 originalUrl

  // 通过受鉴权接口直接得到二进制，因此不再依赖外部下载链接 translatedUrl

  // 根据检测结果分别判断原文/译文是否为 PDF
  // 统一使用 iframe，内置查看器自带滚动与工具栏

  const PdfJsCanvasViewer: React.FC<{ data?: Uint8Array | null; url?: string | null; title?: string }> = ({ data, url }) => {
    const viewerRootRef = useRef<HTMLDivElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const pdfDocRef = useRef<any | null>(null);
    const [page, setPage] = useState(1);
    const [pageCount, setPageCount] = useState<number>(1);
    const [rendering, setRendering] = useState(false);
    const [zoom, setZoom] = useState(1); // 相对于“适应宽度”的缩放倍数
    const [rerenderTick, setRerenderTick] = useState(0); // 容器尺寸变化时触发重渲染
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [pageInput, setPageInput] = useState<string>('1');

    // 加载 PDF 文档（仅在 data 变化时重新加载）
    useEffect(() => {
      let cancelled = false;
      const loadDoc = async () => {
        // 支持从二进制或 URL 加载 PDF
        if (!data && !url) return;
        setRendering(true);
        try {
          let loadingTask: any;
          if (data) {
            const safeData1 = data instanceof Uint8Array ? data.slice(0) : new Uint8Array(data);
            loadingTask = getDocument({ data: safeData1, cMapUrl: '/assets/pdfjs/cmaps/', cMapPacked: true as any, standardFontDataUrl: '/assets/pdfjs/standard_fonts/', enableXfa: true as any, isEvalSupported: false as any });
          } else if (url) {
            loadingTask = getDocument({ url, cMapUrl: '/assets/pdfjs/cmaps/', cMapPacked: true as any, standardFontDataUrl: '/assets/pdfjs/standard_fonts/', enableXfa: true as any, isEvalSupported: false as any });
          }
          if (!loadingTask) return;
          const pdf = await loadingTask.promise;
          if (cancelled) return;
          pdfDocRef.current = pdf;
          setPageCount(pdf.numPages || 1);
          setPage(1);
          setPageInput('1');
          // 首次加载完成后，主动触发一次渲染（避免初始页值未变化导致不渲染）
          setRerenderTick((x) => x + 1);
        } catch (e) {
          console.warn('[Preview][PDF.js] 文档加载失败:', e);
          // 如果是 DataCloneError，尝试关闭 worker 再次加载
          try {
            let loadingTaskNoWorker: any;
            if (data) {
              const safeData2 = data instanceof Uint8Array ? data.slice(0) : new Uint8Array(data);
              loadingTaskNoWorker = getDocument({ data: safeData2, disableWorker: true as any, cMapUrl: '/assets/pdfjs/cmaps/', cMapPacked: true as any, standardFontDataUrl: '/assets/pdfjs/standard_fonts/', enableXfa: true as any, isEvalSupported: false as any });
            } else if (url) {
              loadingTaskNoWorker = getDocument({ url, disableWorker: true as any, cMapUrl: '/assets/pdfjs/cmaps/', cMapPacked: true as any, standardFontDataUrl: '/assets/pdfjs/standard_fonts/', enableXfa: true as any, isEvalSupported: false as any });
            }
            if (!loadingTaskNoWorker) return;
            const pdf2 = await loadingTaskNoWorker.promise;
            if (cancelled) return;
            pdfDocRef.current = pdf2;
            setPageCount(pdf2.numPages || 1);
            setPage(1);
            setPageInput('1');
            console.log('[Preview][PDF.js] 已回退为非 worker 模式并成功加载');
            // 非 worker 模式加载成功后也触发一次渲染
            setRerenderTick((x) => x + 1);
          } catch (e2) {
            console.warn('[Preview][PDF.js] 非 worker 模式仍加载失败:', e2);
            pdfDocRef.current = null;
            setPageCount(1);
            setPage(1);
          }
        } finally {
          if (!cancelled) setRendering(false);
        }
      };
      loadDoc();
      return () => { cancelled = true; };
    }, [data, url]);

    // 监听容器大小变化，触发重渲染以计算“适应宽度”的基准缩放
    useEffect(() => {
      const el = containerRef.current;
      if (!el) return;
      const ro = new ResizeObserver(() => setRerenderTick((x) => x + 1));
      ro.observe(el);
      return () => ro.disconnect();
    }, []);

    // 监听全屏状态变化
    useEffect(() => {
      const handler = () => {
        setIsFullscreen(!!document.fullscreenElement);
        // 全屏切换后也触发一次重渲染，保证按宽度适配
        setRerenderTick((x) => x + 1);
      };
      document.addEventListener('fullscreenchange', handler);
      return () => document.removeEventListener('fullscreenchange', handler);
    }, []);

    // 渲染当前页（在 page、缩放、容器变化 或 文档就绪 时触发）
    useEffect(() => {
      let cancelled = false;
      const render = async () => {
        const pdf = pdfDocRef.current;
        if (!pdf) return;
        setRendering(true);
        try {
          const p = Math.min(Math.max(1, page), pdf.numPages || 1);
          const pageObj = await pdf.getPage(p);
          const rawViewport = pageObj.getViewport({ scale: 1 });
          const containerWidth = Math.max(0, containerRef.current?.clientWidth || 0);
          const targetWidth = containerWidth > 0 ? containerWidth : rawViewport.width;
          const fitScale = Math.max(0.1, targetWidth / rawViewport.width);
          const viewport = pageObj.getViewport({ scale: fitScale * zoom });
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) return;
          const outputScale = window.devicePixelRatio || 1;
          canvas.width = Math.floor(viewport.width * outputScale);
          canvas.height = Math.floor(viewport.height * outputScale);
          canvas.style.width = `${Math.floor(viewport.width)}px`;
          canvas.style.height = `${Math.floor(viewport.height)}px`;
          canvas.style.display = 'block';
          await pageObj.render({ canvasContext: ctx, viewport, transform: [outputScale, 0, 0, outputScale, 0, 0] }).promise;
          if (cancelled) return;
          if (containerRef.current) {
            containerRef.current.innerHTML = '';
            containerRef.current.appendChild(canvas);
          }
        } catch (e) {
          console.warn('[Preview][PDF.js] 渲染失败:', e);
        } finally {
          if (!cancelled) setRendering(false);
        }
      };
      render();
      return () => { cancelled = true; };
    }, [page, zoom, rerenderTick]);

    // 页面状态变化时同步输入框显示
    useEffect(() => {
      setPageInput(String(page));
    }, [page]);

    const zoomIn = () => setZoom((z) => Math.min(5, Math.round((z + 0.1) * 10) / 10));
    const zoomOut = () => setZoom((z) => Math.max(0.25, Math.round((z - 0.1) * 10) / 10));
    const resetZoom = () => setZoom(1);
    const toggleFullscreen = () => {
      const el = viewerRootRef.current;
      if (!el) return;
      if (document.fullscreenElement) {
        document.exitFullscreen?.();
      } else {
        el.requestFullscreen?.();
      }
    };

    return (
      <Box ref={viewerRootRef} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1 }}>
          <Tooltip title={t('translation.preview.prevPage') || '上一页'}>
            <span>
              <IconButton size="small" disabled={page <= 1 || rendering} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                <NavigateBefore fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <TextField
            size="small"
            type="number"
            value={pageInput}
            onChange={(e) => setPageInput(e.target.value)}
            onBlur={() => {
              const n = parseInt(pageInput, 10);
              if (!isNaN(n)) {
                const target = Math.min(Math.max(1, n), pageCount);
                setPage(target);
                setPageInput(String(target));
              } else {
                setPageInput(String(page));
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const n = parseInt(pageInput, 10);
                if (!isNaN(n)) {
                  const target = Math.min(Math.max(1, n), pageCount);
                  setPage(target);
                  setPageInput(String(target));
                } else {
                  setPageInput(String(page));
                }
              }
            }}
            inputProps={{ min: 1, max: pageCount, style: { textAlign: 'center' } }}
            sx={{ width: 72 }}
            disabled={rendering || pageCount < 1}
          />
          <Typography variant="caption"> / {pageCount}</Typography>
          <Tooltip title={t('translation.preview.nextPage') || '下一页'}>
            <span>
              <IconButton size="small" disabled={page >= pageCount || rendering} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>
                <NavigateNext fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Divider flexItem orientation="vertical" sx={{ mx: 1 }} />
          <Typography variant="caption">{Math.round(zoom * 100)}%</Typography>
          <Tooltip title={t('translation.preview.zoomOut') || '缩小'}>
            <span>
              <IconButton size="small" onClick={zoomOut} disabled={rendering}>
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('translation.preview.zoomIn') || '放大'}>
            <span>
              <IconButton size="small" onClick={zoomIn} disabled={rendering}>
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('translation.preview.resetZoom') || '重置缩放'}>
            <span>
              <IconButton size="small" onClick={resetZoom} disabled={rendering}>
                <RestartAlt fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Divider flexItem orientation="vertical" sx={{ mx: 1 }} />
          <Tooltip title={isFullscreen ? (t('translation.preview.exitFullscreen') || '退出全屏') : (t('translation.preview.enterFullscreen') || '全屏')}>
            <IconButton size="small" onClick={toggleFullscreen}>
              {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Stack>
        <Divider />
        <Box ref={containerRef} sx={{ flex: 1, overflow: 'auto', p: 1, minWidth: 0 }} />
      </Box>
    );
  };
  // Stabilize the viewer's component identity with useMemo([]) so it is NOT
  // re-created on every parent re-render. Defined inline without memo, React
  // treats it as a new component type each render -> unmount/remount -> re-init
  // PDFViewer and restart getDocument. pdf.js renders high-res asynchronously
  // (low-res placeholder first, then upgrades), so constant remounting leaves it
  // stuck on the blurry placeholder. It only uses props + imports + its own
  // hooks, so an empty-deps memo is safe.
  const PdfJsOfficialViewer = React.useMemo<React.FC<{ data?: Uint8Array | null; url?: string | null; onPageChange?: (page: number, total: number) => void; exposeViewer?: React.MutableRefObject<any> }>>(() => ({ data, url, onPageChange, exposeViewer }) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const innerRef = useRef<HTMLDivElement | null>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const viewerRef = useRef<any | null>(null);
    const eventBusRef = useRef<any | null>(null);
    const linkServiceRef = useRef<any | null>(null);
    const pdfDocRef = useRef<any | null>(null);
    const prevScaleValueRef = useRef<string | number | null>(null);
    const prevScaleRef = useRef<number | null>(null);
    const [pageCount, setPageCount] = useState<number>(1);
    const [pageInput, setPageInput] = useState<string>('1');
    const [rendering, setRendering] = useState(false);
    const [scale, setScale] = useState<number>(1);
    const [isFullscreen, setIsFullscreen] = useState(false);
    // Tracks when the PDFViewer instance is constructed. loadDoc must wait for
    // it: when this component mounts lazily (e.g. SUBMISSION switching from the
    // collabora Edit view to the PDF View), the document data can arrive before
    // the viewer is ready, and without this flag loadDoc would bail out on
    // `!viewerRef.current` and never retry -> blank pane.
    const [viewerReady, setViewerReady] = useState(false);

    useEffect(() => {
      const container = containerRef.current;
      const inner = innerRef.current;
      if (!container || !inner) return;
      const eventBus = new EventBus();
      const linkService = new PDFLinkService({ eventBus });
      // textLayerMode: 0 disables the selectable text layer — the preview is
      // read-only (canvas image only). The text layer requires pdf.js's
      // official textLayer CSS to position its spans; without it the spans
      // collapse into normal flow and characters overlap. Rendering canvas-only
      // avoids that and is sufficient for a preview.
      const viewer = new PDFViewer({ container, viewer: inner, eventBus, linkService, textLayerMode: 0 as any, annotationMode: 2 as any, renderInteractiveForms: true as any });
      linkService.setViewer(viewer);
      eventBus.on('pagesinit', () => {
        viewer.currentScaleValue = 'page-width';
        setScale(viewer.currentScale || 1);
      });
      eventBus.on('pagesloaded', (e: any) => {
        const count = (e && e.pagesCount) || (viewer as any)?.pagesCount || (pdfDocRef.current?.numPages || 1);
        setPageCount(count);
        if (onPageChange) onPageChange(viewer.currentPageNumber || 1, count);
      });
      eventBus.on('pagechanging', (e: any) => {
        const p = e.pageNumber || viewer.currentPageNumber || 1;
        setPageInput(String(p));
        if (onPageChange) onPageChange(p, (viewer as any)?.pagesCount || pageCount);
      });
      eventBusRef.current = eventBus;
      linkServiceRef.current = linkService;
      viewerRef.current = viewer;
      if (exposeViewer) exposeViewer.current = viewer;
      setViewerReady(true);
      return () => {
        setViewerReady(false);
        viewerRef.current = null;
        linkServiceRef.current = null;
        eventBusRef.current = null;
      };
    }, []);

    useEffect(() => {
      const onFs = () => {
        const el = document.fullscreenElement;
        const entering = !!el && el === wrapperRef.current;
        setIsFullscreen(entering);
        const v = viewerRef.current;
        if (v) {
          try {
            if (entering) {
              v.currentScaleValue = 'page-width';
              setScale(v.currentScale || 1);
            } else {
              const prev = prevScaleRef.current ?? 1;
              setTimeout(() => {
                v.currentScaleValue = String(prev);
                setScale(v.currentScale || prev);
              }, 0);
            }
          } catch {}
        }
      };
      document.addEventListener('fullscreenchange', onFs);
      return () => document.removeEventListener('fullscreenchange', onFs);
    }, []);

    // Load from a single source: prefer the in-memory bytes (data) and ignore
    // url once data is present. data and url can update in separate renders
    // (e.g. refetch sets data then a blob url), and depending on BOTH made
    // loadDoc re-run and interrupt itself mid-setDocument -> the just-rendered
    // pages got cleared, showing a blank pane. Keying the effect on data-or-url
    // (not both) keeps a single, stable load.
    const loadSourceKey = data ? `data:${data.length}` : (url || '');
    useEffect(() => {
      let cancelled = false;
      const loadDoc = async () => {
        if (!viewerReady || !viewerRef.current) return;
        if (!data && !url) return;
        setRendering(true);
        try {
          let task: any;
          if (data) {
            const safe = data instanceof Uint8Array ? data.slice(0) : new Uint8Array(data);
            task = getDocument({ data: safe, cMapUrl: '/assets/pdfjs/cmaps/', cMapPacked: true as any, standardFontDataUrl: '/assets/pdfjs/standard_fonts/', enableXfa: true as any, isEvalSupported: false as any });
          } else if (url) {
            task = getDocument({ url, cMapUrl: '/assets/pdfjs/cmaps/', cMapPacked: true as any, standardFontDataUrl: '/assets/pdfjs/standard_fonts/', enableXfa: true as any, isEvalSupported: false as any });
          }
          if (!task) return;
          const pdf = await task.promise;
          if (cancelled) return;
          pdfDocRef.current = pdf;
          setPageCount(pdf.numPages || 1);
          const viewer = viewerRef.current;
          const linkService = linkServiceRef.current;
          viewer.setDocument(pdf);
          linkService.setDocument(pdf, null);
          viewer.currentPageNumber = 1;
        } catch (e) {
          console.warn('[Preview][OfficialViewer] load failed:', e);
          pdfDocRef.current = null;
          setPageCount(1);
        } finally {
          if (!cancelled) setRendering(false);
        }
      };
      loadDoc();
      return () => { cancelled = true; };
    }, [loadSourceKey, viewerReady]);

    const prevPage = () => {
      const v = viewerRef.current;
      try {
        if (!v) { console.log('[ViewerNav] prev: viewer not ready'); return; }
        const total = (v as any)?.pagesCount || pageCount;
        const cur = v.currentPageNumber || 1;
        const p = Math.max(1, cur - 1);
        console.log('[ViewerNav] prev click', { cur, target: p, total });
        if (p >= 1 && p <= total) {
          const before = v.container?.scrollTop;
          const pv = v.getPageView?.(p - 1);
          const top = pv?.div?.offsetTop;
          v.scrollPageIntoView({ pageNumber: p, allowNegativeOffset: true });
          if (typeof top === 'number' && v.container && typeof before === 'number') {
            setTimeout(() => {
              const after = v.container!.scrollTop;
              console.log('[ViewerNav] prev scroll', { before, top, after });
              if (Math.abs(after - before) < 2) {
                v.container!.scrollTo({ top });
                console.log('[ViewerNav] prev fallback scrollTo', { top });
              }
            }, 0);
          }
          setPageInput(String(p));
        } else {
          console.log('[ViewerNav] prev: target out of range');
        }
      } catch (e) {
        console.error('[ViewerNav] prev error', e);
      }
    };
    const nextPage = () => {
      const v = viewerRef.current;
      try {
        if (!v) { console.log('[ViewerNav] next: viewer not ready'); return; }
        const total = (v as any)?.pagesCount || pageCount;
        const cur = v.currentPageNumber || 1;
        const p = Math.min(total, cur + 1);
        console.log('[ViewerNav] next click', { cur, target: p, total });
        if (p >= 1 && p <= total) {
          const before = v.container?.scrollTop;
          const pv = v.getPageView?.(p - 1);
          const top = pv?.div?.offsetTop;
          v.scrollPageIntoView({ pageNumber: p, allowNegativeOffset: true });
          if (typeof top === 'number' && v.container && typeof before === 'number') {
            setTimeout(() => {
              const after = v.container!.scrollTop;
              console.log('[ViewerNav] next scroll', { before, top, after });
              if (Math.abs(after - before) < 2) {
                v.container!.scrollTo({ top });
                console.log('[ViewerNav] next fallback scrollTo', { top });
              }
            }, 0);
          }
          setPageInput(String(p));
        } else {
          console.log('[ViewerNav] next: target out of range');
        }
      } catch (e) {
        console.error('[ViewerNav] next error', e);
      }
    };
    const applyPageInput = () => {
      const v = viewerRef.current;
      try {
        if (!v) { console.log('[ViewerNav] input: viewer not ready'); return; }
        const total = (v as any)?.pagesCount || pageCount;
        const n = parseInt(pageInput, 10);
        if (!isNaN(n)) {
          const target = Math.min(Math.max(1, n), total);
          console.log('[ViewerNav] input apply', { input: n, target, total });
          const before = v.container?.scrollTop;
          const pv = v.getPageView?.(target - 1);
          const top = pv?.div?.offsetTop;
          v.scrollPageIntoView({ pageNumber: target, allowNegativeOffset: true });
          if (typeof top === 'number' && v.container && typeof before === 'number') {
            setTimeout(() => {
              const after = v.container!.scrollTop;
              console.log('[ViewerNav] input scroll', { before, top, after });
              if (Math.abs(after - before) < 2) {
                v.container!.scrollTo({ top });
                console.log('[ViewerNav] input fallback scrollTo', { top });
              }
            }, 0);
          }
          setPageInput(String(target));
        } else {
          console.log('[ViewerNav] input invalid, reset');
          setPageInput(String(v.currentPageNumber || 1));
        }
      } catch (e) {
        console.error('[ViewerNav] input error', e);
      }
    };
    const zoomIn = () => {
      const v = viewerRef.current;
      if (!v) return;
      const s = Math.min(5, Math.round(((v.currentScale || 1) + 0.1) * 10) / 10);
      v.currentScaleValue = String(s);
      setScale(s);
    };
    const zoomOut = () => {
      const v = viewerRef.current;
      if (!v) return;
      const s = Math.max(0.25, Math.round(((v.currentScale || 1) - 0.1) * 10) / 10);
      v.currentScaleValue = String(s);
      setScale(s);
    };
    const resetZoom = () => {
      const v = viewerRef.current;
      if (!v) return;
      v.currentScaleValue = 'page-width';
      setScale(v.currentScale || 1);
    };
    const toggleFullscreen = async () => {
      const el = wrapperRef.current;
      try {
        const v = viewerRef.current;
        if (!isFullscreen) {
          if (v) {
            prevScaleValueRef.current = v.currentScaleValue;
            prevScaleRef.current = v.currentScale || 1;
          }
          if (el && el.requestFullscreen) {
            await el.requestFullscreen();
          }
        } else {
          if (document.exitFullscreen) {
            await document.exitFullscreen();
          }
        }
      } catch (e) {
        console.error('[ViewerNav] fullscreen error', e);
      }
    };

    return (
      <Box
        ref={wrapperRef}
        sx={{
          height: isFullscreen ? '100vh' : '100%',
          width: isFullscreen ? '100vw' : '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1 }}>
          <Tooltip title={'上一页'}>
            <span>
              <IconButton size="small" disabled={rendering} onClick={prevPage}>
                <NavigateBefore fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <TextField
            size="small"
            type="number"
            value={pageInput}
            onChange={(e) => setPageInput(e.target.value)}
            onBlur={applyPageInput}
            onKeyDown={(e) => { if (e.key === 'Enter') applyPageInput(); }}
            inputProps={{ min: 1, max: pageCount, style: { textAlign: 'center' } }}
            sx={{ width: 72 }}
            disabled={rendering || pageCount < 1}
          />
          <Typography variant="caption"> / {pageCount}</Typography>
          <Tooltip title={'下一页'}>
            <span>
              <IconButton size="small" disabled={rendering} onClick={nextPage}>
                <NavigateNext fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Divider flexItem orientation="vertical" sx={{ mx: 1 }} />
          <Typography variant="caption">{Math.round((scale || 1) * 100)}%</Typography>
          <Tooltip title={'缩小'}>
            <span>
              <IconButton size="small" onClick={zoomOut} disabled={rendering}>
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={'放大'}>
            <span>
              <IconButton size="small" onClick={zoomIn} disabled={rendering}>
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={'重置缩放'}>
            <span>
              <IconButton size="small" onClick={resetZoom} disabled={rendering}>
                <RestartAlt fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Divider flexItem orientation="vertical" sx={{ mx: 1 }} />
          <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
            <span>
              <IconButton size="small" onClick={toggleFullscreen} disabled={rendering}>
                {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <Divider />
        <Box sx={{ flex: 1, position: 'relative', minWidth: 0 }}>
          <Box sx={{ position: 'absolute', inset: 0, overflow: 'auto' }} ref={containerRef}>
            <div className="pdfViewer" ref={innerRef} />
          </Box>
        </Box>
      </Box>
    );
  }, []);
  
  
  
  const ImageZoomPane: React.FC<{ src: string; alt: string }> = ({ src, alt }) => {
    return (
      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
        <TransformWrapper initialScale={1} wheel={{ step: 0.1 }} pinch={{ step: 0.1 }} doubleClick={{ disabled: false }}>
          <TransformComponent wrapperStyle={{ width: '100%', height: '100%' }}>
            <img src={src} alt={alt} style={{ display: 'block', maxWidth: '100%', height: 'auto' }} />
          </TransformComponent>
        </TransformWrapper>
      </Box>
    );
  };
  

  return (
    <Box sx={{ p: 2, position: 'relative' }}>
      {/* 右上角返回按钮 */}
      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
        <Stack direction="row" spacing={1}>
          {SHOW_OPEN_IN_WINDOW && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<OpenInNewIcon fontSize="small" />}
              onClick={(e) => setOpenInNewAnchor(e.currentTarget)}
            >
              {t('translation.preview.openInWindow') || '在新窗口打开'}
            </Button>
          )}
          <Button variant="outlined" size="small" onClick={() => navigate(-1)}>
            {t('common.back') || '返回'}
          </Button>
        </Stack>
        {SHOW_OPEN_IN_WINDOW && (
          <Menu
            anchorEl={openInNewAnchor}
            open={Boolean(openInNewAnchor)}
            onClose={() => setOpenInNewAnchor(null)}
          >
            <MenuItem onClick={openOriginalInNewWindow}>
              {t('translation.preview.original') || '原文'}
            </MenuItem>
            <MenuItem onClick={openTranslatedInNewWindow} disabled={!collaboraTranslatedUrl}>
              {t('translation.preview.translation') || '译文'}
            </MenuItem>
          </Menu>
        )}
      </Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link to="/">{t('translation.navigation.home')}</Link>
        <Link to="/translate-tasks">{t('translation.navigation.translateTasks')}</Link>
        <Typography color="textPrimary">
          {docName || t('translation.preview.title') || 'Preview'}
        </Typography>
      </Breadcrumbs>
      {/* 渲染方式切换：iframe 与 PDF.js（已隐藏控件）*/}
      <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} sx={{ mb: 1, display: 'none' }}>
        {/* <Typography variant="caption">{t('translation.preview.renderMode') || '渲染方式'}</Typography> */}
        <ToggleButtonGroup
          size="small"
          color="primary"
          exclusive
          value={renderMode}
          onChange={(_, val) => { if (val) setRenderMode(val); }}
        >
          <ToggleButton value="iframe">Iframe</ToggleButton>
          <ToggleButton value="pdfjs">PDF.js</ToggleButton>
        </ToggleButtonGroup>
      </Stack>


      {loading && <LinearProgress sx={{ my: 1 }} />}
      {showDebug && (debugOriginal || debugTranslated) && (
        <Paper variant="outlined" sx={{ p: 1, mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle2" sx={{ flex: 1 }}>{t('translation.preview.debugPanel') || '调试信息'}</Typography>
            <Button size="small" onClick={() => {
              const payload = { original: debugOriginal, translated: debugTranslated };
              const txt = JSON.stringify(payload, null, 2);
              navigator.clipboard?.writeText(txt);
              console.log('[Preview][Debug] copied', payload);
            }}>{t('common.copy') || '复制'}</Button>
          </Stack>
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption">Original</Typography>
              <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify(debugOriginal, null, 2)}</pre>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption">Translated</Typography>
              <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify(debugTranslated, null, 2)}</pre>
            </Box>
          </Stack>
        </Paper>
      )}
      <Stack direction="row" spacing={2} sx={{ mt: 2, overflowX: 'hidden', height: 'calc(100vh - 160px)' }}>
        <Paper variant="outlined" sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, height: '100%' }}>
          <Toolbar
            title={t('translation.preview.original') || 'Original'}
          />
          <Divider />
          <Box sx={{ flex: 1, overflow: 'auto', p: 1, minWidth: 0 }}>
            {loading && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('translation.preview.loading') || t('app.loading') || 'Loading...'}
              </Typography>
            )}
            {(() => {
              const renderUrl = originalBlobUrl;
              if (loading) {
                return null;
              }
              if (collaboraOriginalUrl) {
                return (
                  <iframe ref={originalCollaboraRef} src={collaboraOriginalUrl} style={{ width: `${100 / COLLABORA_ZOOM}%`, height: `${100 / COLLABORA_ZOOM}%`, border: 'none', zoom: COLLABORA_ZOOM }} allow="clipboard-read; clipboard-write" title="original-collabora" />
                );
              }
              if (renderMode === 'iframe') {
                if (renderUrl) {
                  const pdf = isOriginalPdf;
                  const img = isOriginalImage;
                  if (pdf) {
                    return (
                      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                        <iframe src={renderUrl} style={{ width: '100%', height: '100%', border: 'none' }} title="original-pdf-iframe" />
                      </Box>
                    );
                  }
                  if (img) {
                    return (
                      <ImageZoomPane
                        src={renderUrl}
                        alt="original-image"
                        onControlsReady={(c) => { originalImageControlsRef.current = c; }}
                        onScaleChange={(s) => setOriginalImageScale(s)}
                      />
                    );
                  }
                  if (isOriginalExcel && renderUrl) {
                    return <XlsxSheetHtmlPreview srcUrl={renderUrl} title="original-excel-html" kind="original" />;
                  }
                  if (isOriginalHtml) {
                    return (
                      <iframe src={renderUrl} style={{ width: '100%', height: '100%', border: 'none' }} title="original-html-iframe" />
                    );
                  }
                  if (isOriginalXhtml) {
                    return (
                      <iframe src={renderUrl} style={{ width: '100%', height: '100%', border: 'none' }} title="original-xhtml-iframe" />
                    );
                  }
                  if (isOriginalXml) {
                    return (
                      <iframe src={renderUrl} style={{ width: '100%', height: '100%', border: 'none' }} title="original-xml-iframe" />
                    );
                  }
                  if (isOriginalTxt) {
                    return (
                      <iframe src={renderUrl} style={{ width: '100%', height: '100%', border: 'none' }} title="original-txt-iframe" />
                    );
                  }
                  return (
                    <Stack sx={{ p: 2 }}>
                      <Typography variant="body2">
                        {t('translation.preview.unsupportedType') || '当前类型的在线预览暂不支持。'}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <a href={renderUrl} target="_blank" rel="noreferrer">{t('translation.preview.openInNewTab') || '在新标签页打开/下载'}</a>
                      </Typography>
                    </Stack>
                  );
                }
                return loading ? null : (
                  <Stack sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('translation.preview.unavailableOriginal') || '原文预览不可用。'}
                    </Typography>
                  </Stack>
                );
              }
              // PDF.js 模式
              if (!originalData && !renderUrl) {
                return (
                  <Stack sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">{t('translation.preview.unsupportedType') || '当前类型的在线预览暂不支持。'}</Typography>
                    {renderUrl && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <a href={renderUrl} target="_blank" rel="noreferrer">{t('translation.preview.openInNewTab') || '在新标签页打开/下载'}</a>
                      </Typography>
                    )}
                  </Stack>
                );
              }
              if (isOriginalImage && renderUrl) {
                return (
                  <ImageZoomPane src={renderUrl} alt="original-image" />
                );
              }
              // Text-like types (txt/html/xml/xhtml/excel) aren't PDFs — pdf.js
              // would show a blank pane. Render them via iframe like iframe mode.
              if (renderUrl && isOriginalExcel) {
                return <XlsxSheetHtmlPreview srcUrl={renderUrl} title="original-excel-html" kind="original" />;
              }
              if (renderUrl && (isOriginalTxt || isOriginalHtml || isOriginalXml || isOriginalXhtml)) {
                return (
                  <iframe src={renderUrl} style={{ width: '100%', height: '100%', border: 'none' }} title="original-text-iframe" />
                );
              }
              return <PdfJsOfficialViewer data={originalData || undefined} url={renderUrl || undefined} onPageChange={(p, t) => { pdfOriginalPageRef.current = { current: p, total: t }; }} exposeViewer={pdfOriginalViewerRef} />;
            })()}
          </Box>
        </Paper>
        <Stack spacing={1} justifyContent="center" alignItems="center" sx={{ px: 0.5 }}>
          <Tooltip title={t('translation.preview.syncToTranslated')}>
            <IconButton size="small" onClick={syncOriginalToTranslated}>
              <ArrowForwardIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('translation.preview.syncToOriginal')}>
            <IconButton size="small" onClick={syncTranslatedToOriginal}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Paper variant="outlined" sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, height: '100%' }}>
          <Stack direction="row" alignItems="center" sx={{ p: 1 }}>
            <Typography variant="subtitle2" sx={{ flex: 1 }}>{t('translation.preview.translation') || 'Translation'}</Typography>
            {collaboraTranslatedUrl && isSubmissionTask && (
              <ToggleButtonGroup
                size="small"
                exclusive
                value={translatedViewMode}
                onChange={(_, val) => { if (val) setTranslatedViewMode(val); }}
              >
                <ToggleButton value="collabora">Edit</ToggleButton>
                <ToggleButton value="pdf">View</ToggleButton>
              </ToggleButtonGroup>
            )}
          </Stack>
          <Divider />
          <Box sx={{ flex: 1, overflow: 'auto', p: 1, minWidth: 0 }}>
            {loading && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('translation.preview.loading') || t('app.loading') || 'Loading...'}
              </Typography>
            )}
            {(() => {
              const renderUrl = translatedBlobUrl;
              if (loading) {
                return null;
              }
              if (collaboraTranslatedUrl && translatedViewMode === 'collabora') {
                if (poppedOutTranslated) {
                  return (
                    <Stack sx={{ p: 3, height: '100%' }} alignItems="center" justifyContent="center" spacing={2}>
                      <Typography variant="body2" color="text.secondary" align="center">
                        {t('translation.preview.editingInWindow') || '译文正在新窗口中编辑。为避免多处协同，此处编辑器已暂时关闭。'}
                      </Typography>
                      <Button variant="outlined" size="small" onClick={bringBackTranslated}>
                        {t('translation.preview.bringBack') || '收回到此处编辑'}
                      </Button>
                    </Stack>
                  );
                }
                return (
                  <iframe ref={translatedCollaboraRef} src={collaboraTranslatedUrl} style={{ width: `${100 / COLLABORA_ZOOM}%`, height: `${100 / COLLABORA_ZOOM}%`, border: 'none', zoom: COLLABORA_ZOOM }} allow="clipboard-read; clipboard-write" title="translated-collabora" />
                );
              }
              if (renderMode === 'iframe') {
                if (renderUrl) {
                  const pdf = isTranslatedPdf;
                  const img = isTranslatedImage;
                  if (pdf) {
                    return (
                      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                        <iframe src={renderUrl} style={{ width: '100%', height: '100%', border: 'none' }} title="translated-pdf-iframe" />
                      </Box>
                    );
                  }
                  if (img) {
                    return (
                      <ImageZoomPane src={renderUrl} alt="translated-image" />
                    );
                  }
                  if (isTranslatedExcel && renderUrl) {
                    return <XlsxSheetHtmlPreview srcUrl={renderUrl} title="translated-excel-html" kind="translated" />;
                  }
                  if (isTranslatedHtml) {
                    return (
                      <iframe src={renderUrl} style={{ width: '100%', height: '100%', border: 'none' }} title="translated-html-iframe" />
                    );
                  }
                  if (isTranslatedXhtml) {
                    return (
                      <iframe src={renderUrl} style={{ width: '100%', height: '100%', border: 'none' }} title="translated-xhtml-iframe" />
                    );
                  }
                  if (isTranslatedXml) {
                    return (
                      <iframe src={renderUrl} style={{ width: '100%', height: '100%', border: 'none' }} title="translated-xml-iframe" />
                    );
                  }
                  if (isTranslatedTxt) {
                    return (
                      <iframe src={renderUrl} style={{ width: '100%', height: '100%', border: 'none' }} title="translated-txt-iframe" />
                    );
                  }
                  return (
                    <Stack sx={{ p: 2 }}>
                      <Typography variant="body2">
                        {t('translation.preview.unsupportedType') || '当前类型的在线预览暂不支持。'}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <a href={renderUrl} target="_blank" rel="noreferrer">{t('translation.preview.openInNewTab') || '在新标签页打开/下载'}</a>
                      </Typography>
                    </Stack>
                  );
                }
                return loading ? null : (
                  <Typography variant="body2" sx={{ p: 2 }}>
                    {t('translation.preview.unavailableTranslated') || '译文预览不可用。'}
                  </Typography>
                );
              }
              // PDF.js 模式
              if (!translatedData && !renderUrl) {
                return (
                  <Stack sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">{t('translation.preview.unsupportedType') || '当前类型的在线预览暂不支持。'}</Typography>
                    {renderUrl && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <a href={renderUrl} target="_blank" rel="noreferrer">{t('translation.preview.openInNewTab') || '在新标签页打开/下载'}</a>
                      </Typography>
                    )}
                  </Stack>
                );
              }
              if (isTranslatedImage && renderUrl) {
                return <ImageZoomPane src={renderUrl} alt="translated-image" />;
              }
              // Text-like types (txt/html/xml/xhtml/excel) are not PDFs — pdf.js
              // can't render them and would show a blank pane. Render them the
              // same way the iframe mode does, before falling back to pdf.js.
              if (renderUrl && isTranslatedExcel) {
                return <XlsxSheetHtmlPreview srcUrl={renderUrl} title="translated-excel-html" kind="translated" />;
              }
              if (renderUrl && (isTranslatedTxt || isTranslatedHtml || isTranslatedXml || isTranslatedXhtml)) {
                return (
                  <iframe src={renderUrl} style={{ width: '100%', height: '100%', border: 'none' }} title="translated-text-iframe" />
                );
              }
              return <PdfJsOfficialViewer data={translatedData || undefined} url={renderUrl || undefined} onPageChange={(p, t) => { pdfTranslatedPageRef.current = { current: p, total: t }; }} exposeViewer={pdfTranslatedViewerRef} />;
            })()}
          </Box>
        </Paper>
      </Stack>
      <Box
        ref={toastRef}
        sx={{
          display: 'none',
          position: 'fixed',
          top: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          bgcolor: 'info.main',
          color: 'info.contrastText',
          px: 3,
          py: 1.5,
          borderRadius: 2,
          boxShadow: 3,
          fontSize: 14,
          fontWeight: 500,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      />
    </Box>
  );
};

// Docx 在线预览已恢复

export default TranslateDocumentPreview;
